import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { formatDividendAmount } from '../../dividends/utils/format-dividend-amount.util';
import { formatDate, formatPeriod, PayoutGroup, statusStyleMap } from './dividends-ledger-item';

const Row: FC<{ border?: boolean; label: ReactNode; value: ReactNode }> = ({
  border = true,
  label,
  value,
}) => (
  <div
    className={twMerge(
      'flex items-center justify-between gap-3 py-3 pl-3 pr-4',
      border && 'border-b border-gray-100',
    )}
  >
    <span className="shrink-0 text-sm font-[450] text-gray-700">{label}</span>
    <span className="min-w-0 truncate text-right text-sm text-gray-700">{value}</span>
  </div>
);

type Props = {
  currencySymbol: string;
  group: PayoutGroup;
  onViewDetails: () => void;
};

// Mobile card for the dividend history — mirrors the desktop table row as a stacked card.
export const DividendsLedgerCard: FC<Props> = ({ currencySymbol, group, onViewDetails }) => {
  const style = statusStyleMap[group.status];

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-4">
        <span className="min-w-0 truncate text-base font-semibold text-gray-700">{group.name}</span>
        <span
          className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-[550] ${style.bg} ${style.text}`}
        >
          {style.label}
        </span>
      </div>

      <Row
        label={<AppFormattedMessage id="PERIOD" />}
        value={formatPeriod(group.frequency, group.year, group.periodIndex)}
      />
      <Row label={<AppFormattedMessage id="PAYOUT_DATE" />} value={formatDate(group.payoutDate)} />
      <Row
        border={false}
        label={<AppFormattedMessage id="TOTAL_DIVIDEND_PAYOUT" />}
        value={`${currencySymbol} ${formatDividendAmount(group.totalAmount)}`}
      />

      <button
        className="flex items-center justify-between bg-brand-25 py-3 pl-3 pr-4"
        onClick={onViewDetails}
        type="button"
      >
        <span className="text-sm font-[450] text-brand-700">
          <AppFormattedMessage id="VIEW_DETAILS" />
        </span>
        <svg className="size-5" fill="none" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4.167 10h11.666M15.833 10l-5-5M15.833 10l-5 5"
            stroke="#2565C8"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </svg>
      </button>
    </div>
  );
};
